import { useState } from 'react';
import { ValidatorFn } from '~src/types';

type FieldConfig = {
  value: string | number;
  validators?: ValidatorFn[];
};

export type FormConfig<TFieldNames extends string> = Record<
  TFieldNames,
  FieldConfig
>;

type FieldState = {
  value: string;
  validators?: ValidatorFn[];
  isValid: boolean;
  wasLeft: boolean;
};

export type FormState<TFieldNames extends string> = Record<
  TFieldNames,
  FieldState
>;

export type ReturnValueType<TFieldNames extends string> = {
  formState: Record<TFieldNames, FieldState>;
  formData: Record<keyof FormConfig<TFieldNames>, string>;
  isFormValid: () => boolean;
  handleChange: (name: TFieldNames, value: string) => void;
  handleBlur: (name: TFieldNames) => void;
};

export default function useForm<TFieldNames extends string>(
  formConfig: FormConfig<TFieldNames>,
): ReturnValueType<TFieldNames> {
  type FieldNames = keyof FormConfig<TFieldNames>;
  type FormStateGeneric = FormState<FieldNames>;

  const [formState, setFormState] = useState<FormStateGeneric>(() => {
    const formState = {} as FormStateGeneric;

    for (const key in formConfig) {
      const fieldConfig = formConfig[key];
      const validators = fieldConfig.validators || [];

      formState[key] = {
        value:
          typeof fieldConfig.value === 'number'
            ? fieldConfig.value.toString()
            : fieldConfig.value,
        validators,
        isValid: true,
        wasLeft: false,
      };
    }

    return formState;
  });

  const formData = {} as Record<FieldNames, string>;

  for (const key in formState) {
    formData[key] = formState[key].value;
  }

  const isFormValid = (): boolean => {
    const values = Object.values<FieldState>(formState);

    return values.every(({ validators, value }): boolean =>
      validators
        ? validators.every((validationFn) => validationFn(value))
        : true,
    );
  };

  const isFieldValid = (
    fieldName: keyof FormStateGeneric,
    value: string,
  ): boolean => {
    const { validators } = formState[fieldName];

    return validators
      ? validators.every((validationFn) => validationFn(value))
      : true;
  };

  const handleChange = (name: keyof FormStateGeneric, value: string) => {
    setFormState((prev) => {
      const prevItem = prev[name];

      return {
        ...prev,
        [name]: {
          ...prevItem,
          value,
          isValid: prevItem.wasLeft ? isFieldValid(name, value) : true,
        },
      };
    });
  };

  const handleBlur = (name: keyof FormStateGeneric) => {
    setFormState((prev) => {
      return {
        ...prev,
        [name]: {
          ...prev[name],
          isValid: isFieldValid(name, formState[name].value),
          wasLeft: true,
        },
      };
    });
  };

  return { formState, formData, isFormValid, handleChange, handleBlur };
}
